import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  fetchJabatan,
  createJabatan,
  updateJabatan,
  deleteJabatan,
} from "../../api/master/Jabatan";

const JabatanPage = () => {
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  const [formTable] = Form.useForm();
  const [formModal] = Form.useForm();
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);

  const searchInput = useRef();

  const [tableLoading, setTableLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const loadData = async () => {
    setTableLoading(true);
    try {
      const apiData = await fetchJabatan();
      setData(
        apiData.map((item) => ({
          key: item.jbt_id_jabatan.toString(),
          namaJabatan: item.jbt_nama_jabatan.trim(),
          gajiPokok: Number(item.jbt_gaji_pokok_minimum),
        }))
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal load data");
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const isEditing = (record) => record.key === editingKey;
  const edit = (record) => {
    formTable.setFieldsValue(record);
    setEditingKey(record.key);
  };
  const cancel = () => setEditingKey("");

  const save = async (key) => {
    setUpdateLoading(true);
    try {
      const values = await formTable.validateFields();
      const res = await updateJabatan(key, {
        jbt_nama_jabatan: values.namaJabatan,
        jbt_gaji_pokok_minimum: values.gajiPokok,
        jbt_updated_by: sessiData[0].user[0].fullname,
      });
      if (res.status) {
        message.success("Update Jabatan Berhasil dilakukan");
        setEditingKey("");
        loadData();
      } else message.error("Update Jabatan Gagal ..!");
    } catch (err) {
      console.error(err);
      message.error("Validasi/Update Jabatan Gagal ..!");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async (key) => {
    try {
      const res = await deleteJabatan(key);
      if (res.status) {
        message.success("Jabatan Berhasil dihapus");
        loadData();
      } else message.error("Jabatan Gagal dihapus..!");
    } catch (err) {
      message.error("Jabatan Gagal dihapus..!");
    }
  };

  const showModal = () => {
    formModal.resetFields();
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    setCreateLoading(true);
    try {
      const values = await formModal.validateFields();
      const res = await createJabatan({
        jbt_nama_jabatan: values.namaJabatan,
        jbt_gaji_pokok_minimum: values.gajiPokok,
        jbt_created_by: sessiData[0].user[0].fullname,
      });
      if (res.status) {
        message.success("Jabatan berhasil ditambahkan");
        setIsModalVisible(false);
        loadData();
      } else message.error("Jabatan Gagal ditambahkan..!");
    } catch (err) {
      message.error("Jabatan Gagal ditambahkan..!");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const getColumnSearch = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            icon={<SearchOutlined />}
          >
            Cari
          </Button>
          <Button
            onClick={() => clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
  });

  const columns = [
    {
      title: "Nama Jabatan",
      dataIndex: "namaJabatan",
      editable: true,
      ...getColumnSearch("namaJabatan"),
    },
    {
      title: "Gaji Pokok",
      dataIndex: "gajiPokok",
      editable: true,
      render: (v) => `Rp ${v.toLocaleString()}`,
      sorter: (a, b) => a.gajiPokok - b.gajiPokok,
    },
    {
      title: "Aksi",
      dataIndex: "aksi",
      width: 100,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              loading={updateLoading}
              onClick={() => save(record.key)}
              type="link"
            >
              Simpan
            </Button>
            <Button onClick={cancel} type="link">
              Batal
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              type="link"
            >
              Edit
            </Button>
            <Popconfirm
              title="Yakin akan menghapus data ini?"
              onConfirm={() => handleDelete(record.key)}
            >
              <Button type="link" danger>
                Hapus
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) =>
    col.editable
      ? {
          ...col,
          onCell: (record) => ({
            record,
            inputType: col.dataIndex === "gajiPokok" ? "number" : "text",
            dataIndex: col.dataIndex,
            title: col.title,
            editing: isEditing(record),
          }),
        }
      : col
  );

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    children,
    ...rest
  }) => {
    const input = inputType === "number" ? <InputNumber /> : <Input />;
    return (
      <td {...rest}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `Isi ${title}` }]}
          >
            {input}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  return (
    <div>
      <Button type="primary" onClick={showModal} style={{ marginBottom: 16 }}>
        Tambah Jabatan
      </Button>

      <Form form={formTable} component={false}>
        <Table
          components={{ body: { cell: EditableCell } }}
          loading={tableLoading}
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            pageSizeOptions: ["10", "20", "40", "60"],
            showSizeChanger: true,
            defaultPageSize: 20,
          }}
          bordered
        />
      </Form>

      {isModalVisible && (
        <Modal
          title="Tambah Jabatan"
          open={isModalVisible}
          confirmLoading={createLoading}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
        >
          <Form form={formModal} layout="vertical">
            <Form.Item
              name="namaJabatan"
              label="Nama Jabatan"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="gajiPokok"
              label="Gaji Pokok"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default JabatanPage;
